import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { KeyRound, AlertTriangle } from 'lucide-react';

/**
 * Card de status de licença — usado no /master (read-only).
 * Recebe `licenseStatus` (payload de /api/license/status) e uma função
 * opcional de formatação de data.
 */
export const LicenseStatusCard = ({ licenseStatus, fmtDate }) => {
  if (!licenseStatus) return null;

  const days = licenseStatus.days_remaining;
  const active = licenseStatus.active;
  const critical = active && days != null && days <= 3;
  const warn = active && days != null && days > 3 && days <= 7;
  const expired = !active;
  const bannerCls = expired
    ? 'bg-red-50 border-red-500 text-red-900'
    : critical
    ? 'bg-red-50 border-red-500 text-red-900'
    : warn
    ? 'bg-amber-50 border-amber-500 text-amber-900'
    : 'bg-emerald-50 border-emerald-500 text-emerald-900';

  const copyFp = () => {
    try {
      navigator.clipboard.writeText(licenseStatus.fingerprint || '');
    } catch (_) { /* clipboard indisponível */ }
  };

  return (
    <>
      {(warn || critical || expired) && (
        <div className={`border-l-4 rounded p-3 flex items-center gap-3 ${bannerCls}`} data-testid="master-license-alert">
          <AlertTriangle size={22} />
          <div className="flex-1">
            <div className="font-bold">
              {expired
                ? 'Licença Local INATIVA'
                : critical
                ? `Licença Local vence em ${days} dia(s)!`
                : `Licença Local vence em ${days} dias`}
            </div>
            <div className="text-xs opacity-90">
              {expired
                ? 'O sistema instalado no cliente está bloqueado. Emita uma nova chave.'
                : 'Emita uma nova chave antes do vencimento para não bloquear o cliente.'}
            </div>
          </div>
        </div>
      )}

      <Card className="p-4" data-testid="master-license-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <KeyRound className="text-emerald-600" size={20} />
            <div className="text-lg font-semibold">Licença Local (Instalação On-Premise)</div>
          </div>
          {active ? (
            <Badge className="bg-emerald-600 text-white" data-testid="master-license-badge">ATIVA</Badge>
          ) : (
            <Badge className="bg-red-600 text-white" data-testid="master-license-badge">INATIVA</Badge>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
          <div>
            <div className="text-xs text-slate-500">CNPJ</div>
            <div className="font-mono text-sm">{licenseStatus.cnpj || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Plano</div>
            <div className="font-bold capitalize">{licenseStatus.plan || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Expira em</div>
            <div className={`font-bold ${critical || expired ? 'text-red-600' : warn ? 'text-amber-600' : 'text-slate-800'}`}>
              {licenseStatus.expires_at ? fmtDate(licenseStatus.expires_at) : '—'}
              {days != null && (days >= 0 ? ` (${days}d)` : ` (vencida há ${Math.abs(days)}d)`)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Fingerprint</div>
            <div className="flex items-center gap-1">
              <div className="font-mono text-[11px] truncate" title={licenseStatus.fingerprint} data-testid="master-license-fingerprint">
                {licenseStatus.fingerprint || '—'}
              </div>
              {licenseStatus.fingerprint && (
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-700 text-xs underline"
                  onClick={copyFp}
                  data-testid="master-license-copy-fp"
                >copiar</button>
              )}
            </div>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          💡 Para emitir/renovar uma chave, use <span className="font-mono">python tools/gerar_licenca.py --cnpj {licenseStatus.cnpj || 'XXX'} --fingerprint {licenseStatus.fingerprint || 'XXX'} --days 30</span>
        </p>
      </Card>
    </>
  );
};
